import { CheckmarkCircleFilled, ErrorCircleFilled, ArrowSyncCircleFilled, WarningFilled, SubtractCircleRegular  } from '@fluentui/react-icons';

export const calculateStatus = (successfulRows, totalRows) => {
  if (!totalRows || totalRows === 0) {
    return { status: 'Failed', color: '#d13438' };
  }
  
  const successRate = (successfulRows / totalRows) * 100;
  
  switch (true) {
    case successRate === 100:
      return { status: 'Success', color: '#107c10' };
    case successRate >= 50:
      return { status: 'Partial', color: '#ffaa44' };
    default:
      return { status: 'Failed', color: '#d13438' };
  }
};

export const getStatusPriority = (status) => {
  const priorities = { 'Failed': 0, 'Partial': 1, 'Success': 2 };
  return priorities[status] || 0;
};

export const getPercentageColor = (percentage) => {
  if (percentage === null) return '#999';
  if (percentage === 100) return '#107c10';
  if (percentage >= 50) return '#ffaa44';
  return '#d13438';
};

export const getStatusIcon = (status, isInProgress = false) => {
  if (isInProgress) {
    return <ArrowSyncCircleFilled style={{ color: '#0078d4', fontSize: '32px' }} />;
  }
  
  switch (status) {
    case 'Success':
      return <CheckmarkCircleFilled style={{ color: '#107c10', fontSize: '32px' }} />;
    case 'Partial':
      return <WarningFilled style={{ color: '#ffaa44', fontSize: '32px' }} />;
    case 'Failed':
      return <ErrorCircleFilled style={{ color: '#d13438', fontSize: '32px' }} />;
    default:
      return <SubtractCircleRegular style={{ color: '#999', fontSize: '32px' }} />;
  }
};

export const getMatchedSyncStatus = (syncInfo) => {
  if (!syncInfo || syncInfo.TotalTables === 0) {
    return {
      statusIcon: <SubtractCircleRegular style={{ color: '#999', fontSize: '32px' }} />,
      tooltipText: 'No sync data available',
      tooltipRatio: null,
      percentage: null
    };
  }

  if (!syncInfo.RunEnded) {
    return {
      statusIcon: <ArrowSyncCircleFilled style={{ color: '#0078d4', fontSize: '32px' }} />,
      tooltipText: 'Sync in progress',
      tooltipRatio: null,
      percentage: null
    };
  }

  const { status } = calculateStatus(
    syncInfo.SuccessfulTables || 0,
    syncInfo.TotalTables || 0
  );

  const successfulTables = syncInfo.SuccessfulTables || 0;
  const totalTables = syncInfo.TotalTables || 0;
  const percentage = totalTables > 0 ? Math.round((successfulTables / totalTables) * 100) : null;

  switch (status) {
    case 'Success':
      return {
        statusIcon: <CheckmarkCircleFilled style={{ color: '#107c10', fontSize: '32px' }} />,
        tooltipText: `Last sync succeeded`,
        tooltipRatio: `${successfulTables} / ${totalTables}`,
        percentage: percentage
      };
    case 'Partial':
      return {
        statusIcon: <WarningFilled style={{ color: '#ffaa44', fontSize: '32px' }} />,
        tooltipText: `Last sync partially succeeded`,
        tooltipRatio: `${successfulTables} / ${totalTables}`,
        percentage: percentage
      };
    case 'Failed':
      return {
        statusIcon: <ErrorCircleFilled style={{ color: '#d13438', fontSize: '32px' }} />,
        tooltipText: `Last sync failed`,
        tooltipRatio: `${successfulTables} / ${totalTables}`,
        percentage: percentage
      };
    default:
      return {
        statusIcon: <SubtractCircleRegular style={{ color: '#999', fontSize: '32px' }} />,
        tooltipText: 'No sync data available',
        tooltipRatio: null,
        percentage: null
      };
  }
};

export const aggregateSyncData = (syncDataList, startedBy) => {
  if (!Array.isArray(syncDataList) || syncDataList.length === 0) {
    return {
      SuccessfulTables: 0,
      TotalTables: 0,
      RunEnded: null
    };
  }

  const result = syncDataList.reduce(
    (acc, entry) => {
      acc.SuccessfulTables += entry.SuccessfulTables || 0;
      acc.TotalTables += entry.TotalTables || 0;

      // If any RunEnded is null, overall RunEnded should be null
      if (entry.RunEnded === null) {
        acc.RunEnded = null;
      }

      return acc;
    },   
    {
      SuccessfulTables: 0,
      TotalTables: 0,
      RunEnded: syncDataList.some(e => e.RunEnded === null)
        ? null
        : syncDataList[syncDataList.length - 1].RunEnded
    }
  );

  return result;
}

export const formatDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 'N/A';
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Truncate to seconds (remove milliseconds) before calculating difference
  const startSeconds = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 
                                start.getHours(), start.getMinutes(), start.getSeconds());
  const endSeconds = new Date(end.getFullYear(), end.getMonth(), end.getDate(),
                              end.getHours(), end.getMinutes(), end.getSeconds());
  
  const diffMs = endSeconds - startSeconds;
  
  if (diffMs < 0) return 'N/A';
  
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  
  return parts.join(' ');
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) {
    return 'N/A';
  }
  
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};